import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Row, Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import { MemoizedTableBodyCell } from './table-body-cell';
import type { FocusedCell, PinningShadowState } from './types';
import { type PinningShadowDirection, getCommonPinningStyles } from './utils';

/**
 * Renders a gradient-based shadow for pinned columns.
 * Uses a pseudo-element approach to avoid vertical bleeding that box-shadow causes.
 */
function PinningShadow({ direction }: { direction: PinningShadowDirection }) {
  if (!direction) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute top-0 h-full w-3"
      style={{
        ...(direction === 'left'
          ? { right: 0, transform: 'translateX(100%)' }
          : { left: 0, transform: 'translateX(-100%)' }),
        background:
          direction === 'left'
            ? 'linear-gradient(to right, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)'
            : 'linear-gradient(to left, rgba(0, 44, 42, 0.06) 0%, rgba(0, 44, 42, 0.02) 50%, transparent 100%)',
      }}
    />
  );
}

// ============================================================================
// VirtualTableBody Props
// ============================================================================

interface VirtualTableBodyProps<TData> {
  table: Table<TData>;
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  estimatedRowHeight: number;
  overscan: number;
  onRowClick?: (row: Row<TData>) => void;
  rowClassName?: string | ((row: Row<TData>) => string);
  keyboardNavigationEnabled: boolean;
  onCellActivate?: (rowIndex: number, columnIndex: number, row: Row<TData>) => void;
  pinningShadows: PinningShadowState;
  headerHeight: number;
  hasNextPage?: boolean;
  skeletonRowCount: number;
}

// ============================================================================
// VirtualTableBody Component
// ============================================================================

function VirtualTableBody<TData>({
  table,
  tableContainerRef,
  estimatedRowHeight,
  overscan,
  onRowClick,
  rowClassName,
  keyboardNavigationEnabled,
  onCellActivate,
  pinningShadows,
  headerHeight,
  hasNextPage,
  skeletonRowCount,
}: VirtualTableBodyProps<TData>) {
  const { rows } = table.getRowModel();
  const skeletonCount = hasNextPage ? skeletonRowCount : 0;
  const visibleColumns = table.getVisibleLeafColumns();
  const totalColumns = visibleColumns.length;
  // Include skeleton rows in navigation bounds so users can navigate into loading area
  const totalRows = rows.length + skeletonCount;

  const leftPinnedColumns = table.getLeftLeafColumns();
  const rightPinnedColumns = table.getRightLeafColumns();
  const leftPinnedCount = leftPinnedColumns.length;
  const rightPinnedCount = rightPinnedColumns.length;

  // Ref for tracking focused cell coordinates - no re-renders on focus change
  const focusedCellRef = React.useRef<FocusedCell | null>(null);

  // Ref to track the last successfully focused cell (for fallback when navigation fails)
  const lastFocusedCellRef = React.useRef<FocusedCell | null>(null);

  // Ref to cancel stale async navigations when a new one starts
  const navigationIdRef = React.useRef(0);

  // Ref to body container for event delegation
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // Ref to access virtualizer's scrollToIndex (set after virtualizer is created)
  const virtualizerScrollToIndexRef = React.useRef<
    ((index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => void) | null
  >(null);

  // Calculate total width of pinned columns for scroll calculations
  const leftPinnedWidth = React.useMemo(() => {
    return leftPinnedColumns.reduce((acc, col) => acc + col.getSize(), 0);
  }, [leftPinnedColumns]);

  const rightPinnedWidth = React.useMemo(() => {
    return rightPinnedColumns.reduce((acc, col) => acc + col.getSize(), 0);
  }, [rightPinnedColumns]);

  // Scrollbar height (12px + 1px border) for scroll calculations
  const scrollbarHeight = 13;

  // Store scroll context in ref to avoid effect dependencies
  const scrollContextRef = React.useRef({
    leftPinnedCount,
    rightPinnedCount,
    totalColumns,
    leftPinnedWidth,
    rightPinnedWidth,
    headerHeight,
    scrollbarHeight,
  });
  scrollContextRef.current = {
    leftPinnedCount,
    rightPinnedCount,
    totalColumns,
    leftPinnedWidth,
    rightPinnedWidth,
    headerHeight,
    scrollbarHeight,
  };

  // Helper to find cell element by coordinates
  const getCellElement = React.useCallback(
    (rowIndex: number, columnIndex: number): HTMLElement | null => {
      return bodyRef.current?.querySelector(
        `[data-cell-row="${rowIndex}"][data-cell-col="${columnIndex}"]`,
      ) as HTMLElement | null;
    },
    [],
  );

  // Scroll cell into view and focus it
  const scrollAndFocusCell = React.useCallback(
    (rowIndex: number, columnIndex: number) => {
      const container = tableContainerRef.current;
      if (!container) {
        return;
      }

      // Increment navigation ID to cancel any pending async navigations
      const currentNavId = ++navigationIdRef.current;

      const {
        leftPinnedCount: leftCount,
        rightPinnedCount: rightCount,
        totalColumns: colCount,
        leftPinnedWidth: leftWidth,
        rightPinnedWidth: rightWidth,
        headerHeight: hHeight,
        scrollbarHeight: sbHeight,
      } = scrollContextRef.current;

      const isLeftPinned = columnIndex < leftCount;
      const isRightPinned = columnIndex >= colCount - rightCount;

      // Helper to adjust scroll and focus a cell element
      const adjustScrollAndFocus = (cellElement: HTMLElement) => {
        const cellRect = cellElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        let targetScrollLeft = container.scrollLeft;
        let targetScrollTop = container.scrollTop;

        // Horizontal scroll
        if (isLeftPinned) {
          targetScrollLeft = 0;
        } else if (isRightPinned) {
          targetScrollLeft = container.scrollWidth - container.clientWidth;
        } else {
          const visibleLeft = containerRect.left + leftWidth;
          const visibleRight = containerRect.right - rightWidth;
          if (cellRect.left < visibleLeft) {
            targetScrollLeft = Math.max(0, container.scrollLeft + (cellRect.left - visibleLeft));
          } else if (cellRect.right > visibleRight) {
            targetScrollLeft = container.scrollLeft + (cellRect.right - visibleRight);
          }
        }

        // Vertical scroll
        const visibleTop = containerRect.top + hHeight;
        const visibleBottom = containerRect.bottom - sbHeight;
        if (cellRect.top < visibleTop) {
          targetScrollTop = container.scrollTop + (cellRect.top - visibleTop);
        } else if (cellRect.bottom > visibleBottom) {
          targetScrollTop = container.scrollTop + (cellRect.bottom - visibleBottom);
        }

        const needsScroll =
          targetScrollLeft !== container.scrollLeft || targetScrollTop !== container.scrollTop;
        if (needsScroll) {
          container.scrollTo({ left: targetScrollLeft, top: targetScrollTop });
        }

        cellElement.focus();
        lastFocusedCellRef.current = { rowIndex, columnIndex };
      };

      // Helper to restore focus to last known good cell
      const restoreFocusToLastCell = () => {
        const lastCell = lastFocusedCellRef.current;
        if (!lastCell) {
          return;
        }

        // Try to scroll to last cell and focus it
        virtualizerScrollToIndexRef.current?.(lastCell.rowIndex, { align: 'center' });
        setTimeout(() => {
          const lastCellElement = getCellElement(lastCell.rowIndex, lastCell.columnIndex);
          if (lastCellElement) {
            lastCellElement.focus();
          }
        }, 50);
      };

      // First try to find the cell - it might already be rendered
      const existingCell = getCellElement(rowIndex, columnIndex);
      if (existingCell) {
        adjustScrollAndFocus(existingCell);
        return;
      }

      // Cell doesn't exist - use virtualizer to scroll to it (for virtualized tables)
      const scrollToIndex = virtualizerScrollToIndexRef.current;
      if (scrollToIndex) {
        // Use 'center' alignment to ensure the row is definitely in the visible range
        scrollToIndex(rowIndex, { align: 'center' });

        // Retry mechanism with setTimeout to let scroll events fire and React re-render
        let attempts = 0;
        const maxAttempts = 15;

        const tryFocusCell = () => {
          // Cancel if a newer navigation started
          if (navigationIdRef.current !== currentNavId) {
            return;
          }

          const cellElement = getCellElement(rowIndex, columnIndex);
          if (cellElement) {
            adjustScrollAndFocus(cellElement);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(tryFocusCell, 16); // ~1 frame at 60fps
          } else {
            // Failed after max attempts - restore focus to prevent losing it
            restoreFocusToLastCell();
          }
        };

        // Start trying after a short delay to let scroll events fire
        setTimeout(tryFocusCell, 32);
      }
    },
    [getCellElement, tableContainerRef],
  );

  // Navigate to a cell
  const navigateToCell = React.useCallback(
    (rowIndex: number, columnIndex: number) => {
      focusedCellRef.current = { rowIndex, columnIndex };
      // Update lastFocusedCellRef immediately so that rapid key presses
      // navigate from the intended target, not the last successfully focused cell
      lastFocusedCellRef.current = { rowIndex, columnIndex };
      scrollAndFocusCell(rowIndex, columnIndex);
    },
    [scrollAndFocusCell],
  );

  // Event delegation handler for keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!keyboardNavigationEnabled) {
        return;
      }

      const target = e.target as HTMLElement;
      let rowIndex = Number.parseInt(target.dataset.cellRow ?? '', 10);
      let columnIndex = Number.parseInt(target.dataset.cellCol ?? '', 10);

      // If target doesn't have cell data, try to use last focused cell as fallback
      // This handles the case where focus was lost due to virtualization
      if (Number.isNaN(rowIndex) || Number.isNaN(columnIndex)) {
        const lastCell = lastFocusedCellRef.current;
        if (lastCell) {
          rowIndex = lastCell.rowIndex;
          columnIndex = lastCell.columnIndex;
        } else {
          return;
        }
      }

      // Get the row - may be undefined for skeleton rows
      const row = rows[rowIndex];

      // Handle copy (Cmd+C / Ctrl+C) - only for real data rows
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        if (!row) {
          return; // Can't copy from skeleton rows
        }
        e.preventDefault();
        const visibleCells = row.getVisibleCells();
        const cell = visibleCells[columnIndex];
        if (cell) {
          // Prefer visible text content (which may be translated) over raw cell value
          let textToCopy = target.textContent?.trim() || '';

          if (!textToCopy) {
            const cellValue = cell.getValue();
            if (cellValue != null && cellValue !== '') {
              textToCopy = String(cellValue);
            } else {
              const columnId = cell.column.id;
              if (columnId && row.original) {
                const rawValue = (row.original as Record<string, unknown>)[columnId];
                textToCopy = rawValue != null ? String(rawValue) : '';
              }
            }
          }

          navigator.clipboard.writeText(textToCopy).catch((err) => {
            console.error('Failed to copy cell content:', err);
          });
        }
        return;
      }

      let nextRow = rowIndex;
      let nextCol = columnIndex;
      let handled = false;

      switch (e.key) {
        case 'ArrowUp':
          if (rowIndex > 0) {
            nextRow = rowIndex - 1;
            handled = true;
          }
          break;
        case 'ArrowDown':
          if (rowIndex < totalRows - 1) {
            nextRow = rowIndex + 1;
            handled = true;
          }
          break;
        case 'ArrowLeft':
          if (columnIndex > 0) {
            nextCol = columnIndex - 1;
            handled = true;
          }
          break;
        case 'ArrowRight':
          if (columnIndex < totalColumns - 1) {
            nextCol = columnIndex + 1;
            handled = true;
          }
          break;
        case 'Tab':
          if (e.shiftKey) {
            if (columnIndex > 0) {
              nextCol = columnIndex - 1;
              handled = true;
            } else if (rowIndex > 0) {
              nextRow = rowIndex - 1;
              nextCol = totalColumns - 1;
              handled = true;
            }
          } else {
            if (columnIndex < totalColumns - 1) {
              nextCol = columnIndex + 1;
              handled = true;
            } else if (rowIndex < totalRows - 1) {
              nextRow = rowIndex + 1;
              nextCol = 0;
              handled = true;
            }
          }
          break;
        case 'Enter':
          // Only activate real data rows, not skeleton rows
          if (row) {
            e.preventDefault();
            onCellActivate?.(rowIndex, columnIndex, row);
          }
          return;
        case 'Home':
          if (e.ctrlKey || e.metaKey) {
            nextRow = 0;
            nextCol = 0;
          } else {
            nextCol = 0;
          }
          handled = true;
          break;
        case 'End':
          if (e.ctrlKey || e.metaKey) {
            nextRow = totalRows - 1;
            nextCol = totalColumns - 1;
          } else {
            nextCol = totalColumns - 1;
          }
          handled = true;
          break;
      }

      if (handled) {
        e.preventDefault();
        navigateToCell(nextRow, nextCol);
      }
    },
    [keyboardNavigationEnabled, totalRows, totalColumns, rows, onCellActivate, navigateToCell],
  );

  // Handle focus events to update ref and manage roving tabindex
  const handleFocus = React.useCallback(
    (e: React.FocusEvent) => {
      const target = e.target as HTMLElement;
      const rowIndex = Number.parseInt(target.dataset.cellRow ?? '', 10);
      const columnIndex = Number.parseInt(target.dataset.cellCol ?? '', 10);

      if (Number.isNaN(rowIndex) || Number.isNaN(columnIndex)) {
        return;
      }

      // Update previous cell's tabindex to -1
      const prevCell = focusedCellRef.current;
      if (prevCell && (prevCell.rowIndex !== rowIndex || prevCell.columnIndex !== columnIndex)) {
        const prevElement = getCellElement(prevCell.rowIndex, prevCell.columnIndex);
        if (prevElement) {
          prevElement.setAttribute('tabindex', '-1');
        }
      }

      // Update current cell's tabindex to 0
      target.setAttribute('tabindex', '0');
      focusedCellRef.current = { rowIndex, columnIndex };
      lastFocusedCellRef.current = { rowIndex, columnIndex };
    },
    [getCellElement],
  );

  // Handle click events via delegation to focus clicked cell
  const handleClick = React.useCallback((e: React.MouseEvent) => {
    // Find the cell element (could be the target or an ancestor)
    const target = e.target as HTMLElement;
    const cellElement = target.closest('[data-cell-row]') as HTMLElement | null;

    if (!cellElement) {
      return;
    }

    // Don't steal focus from input/textarea elements (e.g., when cell is in edit mode)
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    cellElement.focus();
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: rows.length + skeletonCount,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
  });

  // Store scrollToIndex ref for use in scrollAndFocusCell
  virtualizerScrollToIndexRef.current = rowVirtualizer.scrollToIndex;

  // Attach keyboard handler to document to capture events when focus escapes cells
  // This happens when virtualization unmounts the focused cell and focus moves to document.body
  React.useEffect(() => {
    if (!keyboardNavigationEnabled) {
      return;
    }

    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      // Only handle arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }

      // Check if focus is on a table cell - if so, let the normal handler deal with it
      const target = e.target as HTMLElement;
      if (target.dataset.cellRow !== undefined) {
        return;
      }

      // Check if we have a navigation context (user was navigating the table)
      const lastCell = lastFocusedCellRef.current;
      if (!lastCell) {
        return;
      }

      // Check if the body container is in the DOM
      const body = bodyRef.current;
      const container = tableContainerRef.current;
      if (!body) {
        return;
      }

      // If focus is inside the table body (e.g., editing input or popover),
      // let the bubbling handler deal with it to prevent double-navigation
      if (body.contains(document.activeElement)) {
        return;
      }

      // Only handle when focus escaped to document.body or is on the container
      const focusOnContainer = container === document.activeElement;
      const focusEscaped = document.activeElement === document.body;
      if (!focusOnContainer && !focusEscaped) {
        return;
      }

      const { rowIndex, columnIndex } = lastCell;
      let nextRow = rowIndex;
      let nextCol = columnIndex;
      let handled = false;

      switch (e.key) {
        case 'ArrowUp':
          if (rowIndex > 0) {
            nextRow = rowIndex - 1;
            handled = true;
          }
          break;
        case 'ArrowDown':
          if (rowIndex < totalRows - 1) {
            nextRow = rowIndex + 1;
            handled = true;
          }
          break;
        case 'ArrowLeft':
          if (columnIndex > 0) {
            nextCol = columnIndex - 1;
            handled = true;
          }
          break;
        case 'ArrowRight':
          if (columnIndex < totalColumns - 1) {
            nextCol = columnIndex + 1;
            handled = true;
          }
          break;
      }

      if (handled) {
        e.preventDefault();
        navigateToCell(nextRow, nextCol);
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [keyboardNavigationEnabled, totalRows, totalColumns, navigateToCell]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Render cells for a row - simplified without focus state
  const renderCells = React.useCallback(
    (row: Row<TData>, rowIndex: number, totalRowCount: number) => {
      const isLastRow = rowIndex === totalRowCount - 1;
      return row.getVisibleCells().map((cell, columnIndex) => {
        // First cell is focusable by default (roving tabindex)
        const isFirstCell = rowIndex === 0 && columnIndex === 0;
        const tabIndex = keyboardNavigationEnabled ? (isFirstCell ? 0 : -1) : undefined;

        return (
          <MemoizedTableBodyCell
            key={cell.id}
            cell={cell}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            tabIndex={tabIndex}
            pinningShadows={pinningShadows}
            isLastRow={isLastRow}
          />
        );
      });
    },
    [keyboardNavigationEnabled, pinningShadows],
  );

  // For small datasets, don't virtualize
  if (rows.length <= 50) {
    return (
      <div
        ref={bodyRef}
        className="relative"
        onClick={keyboardNavigationEnabled ? handleClick : undefined}
        onKeyDown={keyboardNavigationEnabled ? handleKeyDown : undefined}
        onFocus={keyboardNavigationEnabled ? handleFocus : undefined}
      >
        {rows.map((row, rowIndex) => {
          const resolvedClassName =
            typeof rowClassName === 'function' ? rowClassName(row) : rowClassName;
          const isRowClickable = onRowClick && !keyboardNavigationEnabled;

          return (
            <div
              key={row.id}
              className={cn(
                'flex w-full transition-colors [&:has(.virtual-table-cell:focus)]:z-[1]',
                isRowClickable && 'cursor-pointer',
                resolvedClassName,
              )}
              style={{ height: estimatedRowHeight }}
              onClick={isRowClickable ? () => onRowClick(row) : undefined}
              onKeyDown={
                isRowClickable
                  ? (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              tabIndex={isRowClickable ? 0 : undefined}
            >
              {renderCells(row, rowIndex, rows.length)}
            </div>
          );
        })}
        {/* Skeleton rows for small datasets - using index as key is acceptable since skeletons have no unique IDs */}
        {skeletonCount > 0 &&
          Array.from({ length: skeletonCount }).map((_, idx) => {
            const skeletonRowIndex = rows.length + idx;
            const isLastSkeletonRow = idx === skeletonCount - 1;
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no unique IDs and never reorder
                key={`skeleton-${idx}`}
                className="flex w-full transition-colors [&:has(.virtual-table-cell:focus)]:z-[1]"
                style={{ height: estimatedRowHeight }}
              >
                {visibleColumns.map((column, colIndex) => {
                  const tabIndex = keyboardNavigationEnabled ? -1 : undefined;
                  const { styles: pinningStyles, shadowDirection } = getCommonPinningStyles(
                    column,
                    pinningShadows,
                  );
                  return (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cells have no unique IDs and never reorder
                      key={`skeleton-cell-${idx}-${colIndex}`}
                      data-cell-row={skeletonRowIndex}
                      data-cell-col={colIndex}
                      tabIndex={tabIndex}
                      className="virtual-table-cell group shrink-0 focus:outline-none"
                      style={{
                        width: `calc(var(--col-${column.id}-size) * 1px)`,
                        left: `calc(var(--col-${column.id}-start) * 1px)`,
                        ...pinningStyles,
                      }}
                    >
                      <div
                        className={cn(
                          'flex min-h-10 flex-none items-center border border-transparent border-r-primary-5 bg-white px-3',
                          'group-focus:rounded-xs group-focus:border-highlight-default group-focus:bg-highlight-light',
                          !isLastSkeletonRow && 'border-b-primary-5',
                        )}
                      >
                        <Skeleton className="h-4 w-3/4 bg-primary-10" />
                      </div>
                      <PinningShadow direction={shadowDirection} />
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
    );
  }

  return (
    <div
      ref={bodyRef}
      className="relative"
      style={{ height: totalSize }}
      onClick={keyboardNavigationEnabled ? handleClick : undefined}
      onKeyDown={keyboardNavigationEnabled ? handleKeyDown : undefined}
      onFocus={keyboardNavigationEnabled ? handleFocus : undefined}
    >
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];

        // Render skeleton row if beyond real data
        if (!row) {
          const isLastSkeletonRow = virtualRow.index === rows.length + skeletonCount - 1;
          return (
            <div
              key={`skeleton-${virtualRow.index}`}
              data-index={virtualRow.index}
              className="absolute top-0 left-0 flex w-full transition-colors [&:has(.virtual-table-cell:focus)]:z-[1]"
              style={{
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {visibleColumns.map((column, colIndex) => {
                const tabIndex = keyboardNavigationEnabled ? -1 : undefined;
                const { styles: pinningStyles, shadowDirection } = getCommonPinningStyles(
                  column,
                  pinningShadows,
                );
                return (
                  <div
                    key={`skeleton-cell-${virtualRow.index}-${colIndex}`}
                    data-cell-row={virtualRow.index}
                    data-cell-col={colIndex}
                    tabIndex={tabIndex}
                    className="virtual-table-cell group shrink-0 focus:outline-none"
                    style={{
                      width: `calc(var(--col-${column.id}-size) * 1px)`,
                      left: `calc(var(--col-${column.id}-start) * 1px)`,
                      ...pinningStyles,
                    }}
                  >
                    <div
                      className={cn(
                        'flex min-h-10 flex-none items-center border border-transparent border-r-primary-5 bg-white px-3',
                        'group-focus:rounded-xs group-focus:border-highlight-default group-focus:bg-highlight-light',
                        !isLastSkeletonRow && 'border-b-primary-5',
                      )}
                    >
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                    <PinningShadow direction={shadowDirection} />
                  </div>
                );
              })}
            </div>
          );
        }

        const resolvedClassName =
          typeof rowClassName === 'function' ? rowClassName(row) : rowClassName;
        const isRowClickable = onRowClick && !keyboardNavigationEnabled;

        return (
          <div
            key={row.id}
            data-index={virtualRow.index}
            className={cn(
              'absolute top-0 left-0 flex w-full transition-colors [&:has(.virtual-table-cell:focus)]:z-[1]',
              isRowClickable && 'cursor-pointer',
              resolvedClassName,
            )}
            style={{
              height: virtualRow.size,
              transform: `translateY(${virtualRow.start}px)`,
            }}
            onClick={isRowClickable ? () => onRowClick(row) : undefined}
            onKeyDown={
              isRowClickable
                ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined
            }
            tabIndex={isRowClickable ? 0 : undefined}
          >
            {renderCells(row, virtualRow.index, rows.length)}
          </div>
        );
      })}
    </div>
  );
}

// Memoize VirtualTableBody to prevent unnecessary re-renders
export const MemoizedVirtualTableBody = React.memo(VirtualTableBody) as typeof VirtualTableBody;
